<template>
  <div class="bg-white border border-gray-50 rounded overflow-hidden">
    <!-- A <table> with a percentage colgroup summing to 100, per
         app_ui/tables. Not CSS grid: three hand-matched grid templates on the
         Transform board silently put a money column under the wrong header. -->
    <table class="w-full text-sm">
      <colgroup>
        <col style="width: 7%" />
        <col style="width: 34%" />
        <col style="width: 13%" />
        <col style="width: 13%" />
        <col style="width: 11%" />
        <col style="width: 12%" />
        <col style="width: 10%" />
      </colgroup>
      <thead>
        <tr class="bg-gray-25 border-b border-gray-50 text-left text-gray-600" style="font-size: 9.5px">
          <th class="pl-4 py-2 font-medium uppercase tracking-wide">ID</th>
          <th class="py-2 font-medium uppercase tracking-wide">Requirement</th>
          <th class="py-2 font-medium uppercase tracking-wide">Queue</th>
          <th class="py-2 font-medium uppercase tracking-wide">Delivery</th>
          <th class="py-2 font-medium uppercase tracking-wide">Tenant</th>
          <th class="py-2 font-medium uppercase tracking-wide">Owner</th>
          <th class="pr-4 py-2 font-medium uppercase tracking-wide"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!tickets.length">
          <td colspan="7" class="pl-4 py-8 text-gray-600">
            No requirements in this queue.
          </td>
        </tr>
        <template v-for="ticket in tickets" :key="ticket.id">
          <tr
            class="border-b border-gray-25 hover:bg-gray-10 cursor-pointer align-middle"
            @click="toggle(ticket.id)"
          >
            <td class="pl-4 py-3 text-gray-600">{{ ticket.requirement }}</td>
            <td class="py-3">
              <div class="text-gray-900">{{ ticket.requirement_title }}</div>
              <div class="text-gray-600 mt-0.5" style="font-size: 10px">
                {{ ticket.requirement_type }} · {{ ticket.requirement_status }}
                <span v-if="ticket.moved" class="text-primary-700">
                  · moved from {{ previousLabel(ticket) }}
                </span>
              </div>
            </td>
            <td class="py-3">
              <span
                v-for="queue in ticket.queues"
                :key="queue"
                class="inline-block px-2 py-0.5 rounded mr-1"
                style="font-size: 10px"
                :class="queueClass(queue)"
              >{{ queueLabel(queue) }}</span>
            </td>
            <td class="py-3 text-gray-700" style="font-size: 11px">
              {{ deliveryLabel(ticket.delivery) }}
            </td>
            <td class="py-3 text-gray-700" style="font-size: 11px">
              {{ ticket.tenant_name || '—' }}
            </td>
            <td class="py-3 text-gray-700" style="font-size: 11px">
              {{ ticket.owner_name || 'Unassigned' }}
            </td>
            <td class="pr-4 py-3 text-right">
              <button
                v-if="isCloseable(ticket)"
                type="button"
                class="px-2 py-1 rounded border border-neutral-700 text-neutral-700 hover:bg-neutral-50"
                style="font-size: 10px"
                @click.stop="confirmClose(ticket)"
              >
                Close
              </button>
            </td>
          </tr>
          <tr v-if="expandedId === ticket.id" class="border-b border-gray-50 bg-gray-10">
            <td colspan="7" class="px-4 py-4">
              <p class="text-gray-900 mb-3">{{ ticket.story }}</p>

              <!-- An Unknown verdict carries no recommendation by design. The
                   agent could not establish the state; suggesting an action on
                   that basis is the thing this system exists to stop. -->
              <p v-if="isUnknown(ticket)" class="text-gray-600 italic mb-3">
                Evidence was insufficient to determine this requirement's state,
                so there is no recommended action.
              </p>
              <p v-else-if="ticket.recommended_action" class="text-gray-800 mb-3">
                <span class="font-medium">Recommended:</span>
                {{ ticket.recommended_action }}
              </p>

              <div class="flex flex-wrap gap-x-6 gap-y-1 text-gray-700 mb-3" style="font-size: 11px">
                <span>Validity: <b>{{ ticket.validity }}</b></span>
                <span>Demand: <b>{{ ticket.demand }}</b></span>
                <span>Delivery: <b>{{ ticket.delivery }}</b></span>
                <span>Attention: <b>{{ ticket.attention }}</b></span>
                <span>Evidence: <b>{{ ticket.evidence_quality }}</b></span>
              </div>

              <div v-if="ticket.citations && ticket.citations.length">
                <div class="text-gray-600 uppercase tracking-wide mb-1" style="font-size: 9.5px">
                  Evidence
                </div>
                <ul class="space-y-1">
                  <li
                    v-for="(citation, index) in ticket.citations"
                    :key="index"
                    class="text-gray-700"
                    style="font-size: 11px"
                  >
                    <span class="font-mono text-gray-900">
                      {{ citation.namespace }}:{{ citation.id }}
                    </span>
                    — {{ citation.why_it_matters }}
                  </li>
                </ul>
              </div>
              <p v-else class="text-gray-600" style="font-size: 11px">
                No typed evidence was recorded for this verdict.
              </p>

              <p
                v-if="ticket.record_changed_since_investigation"
                class="mt-3 text-yellow-dark"
                style="font-size: 11px"
              >
                This requirement changed after Hades investigated it — the verdict
                may describe a record that has since moved.
              </p>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <ConfirmationModal
      :is-visible="!!pendingClose"
      :message="closeMessage"
      @confirm="doClose"
      @cancel="pendingClose = null"
    />
  </div>
</template>

<script>
import axios from 'axios';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import {
  CLOSEABLE_QUEUES,
  DELIVERY_LABELS,
  QUEUE_CLASSES,
  QUEUE_LABELS,
} from './hades_constants';

export default {
  name: 'Hades_TicketTable',
  components: { ConfirmationModal },
  props: {
    tickets: { type: Array, required: true },
  },
  emits: ['closed'],
  data() {
    return { expandedId: null, pendingClose: null };
  },
  computed: {
    closeMessage() {
      const ticket = this.pendingClose;
      if (!ticket) return '';
      const citations = (ticket.citations || [])
        .map((c) => `${c.namespace}:${c.id}`)
        .join(', ');
      return [
        `#${ticket.requirement} — ${ticket.requirement_title}`,
        '',
        `Hades found this ${this.deliveryLabel(ticket.delivery)}.`,
        citations ? `Evidence: ${citations}` : 'No typed evidence recorded.',
        '',
        'The evidence will be posted as an internal note on the requirement.',
      ].join('\n');
    },
  },
  methods: {
    toggle(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    queueLabel(queue) {
      return QUEUE_LABELS[queue] || queue;
    },
    queueClass(queue) {
      return QUEUE_CLASSES[queue] || 'bg-gray-50 text-gray-700';
    },
    deliveryLabel(delivery) {
      return DELIVERY_LABELS[delivery] || delivery;
    },
    previousLabel(ticket) {
      return (ticket.previous_queues || []).map(this.queueLabel).join(', ') || '—';
    },
    isUnknown(ticket) {
      return (ticket.queues || []).includes('needs_evidence');
    },
    isCloseable(ticket) {
      return (ticket.queues || []).some((q) => CLOSEABLE_QUEUES.includes(q));
    },
    confirmClose(ticket) {
      this.pendingClose = ticket;
    },
    async doClose() {
      const ticket = this.pendingClose;
      this.pendingClose = null;
      try {
        await axios.post('/api/hades/close/', { hades_ticket_id: ticket.id });
        this.$emit('closed');
      } catch (e) {
        // 409 is the stale-read guard: the requirement moved after Hades looked
        // at it. Surface it rather than retrying — the verdict needs refreshing,
        // not forcing.
        const detail = e.response?.data?.detail || 'Could not close the requirement.';
        window.alert(detail);
      }
    },
  },
};
</script>
